import { cn } from '@/lib/utils';

import { TotemLabel } from '../TotemLabel';
import { BasicInput } from '../BasicInput';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';

import DatePicker, { registerLocale } from 'react-datepicker';
import type { DatePickerProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { fr } from 'date-fns/locale';

registerLocale('fr', fr);

const isRangeDate = (date: Date | Date[] | null) => {
    return Array.isArray(date);
};

type DetailFormDatePickerProps = Omit<DatePickerProps, 'onChange' | 'selectsMultiple' | 'selectsRange'> & {
    label?: string;
    sublabel?: string;
    defaultValue?: Date | string | null;
    placeholder?: string;
    minDate?: Date;
    name: string;
    isDisabled?: boolean;
    error?: string;
    control: Control;
    transformValueToDate?: (value: string | null) => Date | null;
    transformDateToValue?: (date: Date | null) => string | null;
    dateFormat?: string;
    compact?: boolean;
    dataTestId?: string;
};

export const DetailFormDatePicker = ({
    label,
    sublabel,
    placeholder,
    defaultValue,
    name,
    isDisabled,
    error,
    control,
    minDate,
    transformValueToDate,
    transformDateToValue,
    dateFormat = 'dd/MM/yyyy',
    compact = false,
    dataTestId = `date-picker-${name}`,
    ...props
}: DetailFormDatePickerProps) => {
    return (
        <div className="flex flex-col text-foreground" data-testid={dataTestId}>
            <div className="flex flex-col">
                {label ? <TotemLabel>{label}</TotemLabel> : null}
                {sublabel ? <span className="text-muted-foreground text-[13px]">{sublabel}</span> : null}
            </div>
            <div className="relative mt-[5px]">
                <Controller
                    control={control}
                    name={name}
                    defaultValue={defaultValue}
                    render={({ field: { onChange, value } }) => (
                        <DatePicker
                            selected={transformValueToDate ? transformValueToDate(value) : value}
                            onChange={(date: any) => {
                                if (isRangeDate(date)) {
                                    return;
                                }
                                if (transformDateToValue) {
                                    onChange(transformDateToValue(date as Date | null));
                                } else {
                                    onChange(date);
                                }
                            }}
                            placeholderText={placeholder}
                            locale="fr"
                            disabled={isDisabled}
                            dateFormat={dateFormat}
                            customInput={<BasicInput className={cn(compact && 'px-2 py-1.5 text-base h-8')} />}
                            minDate={minDate}
                            wrapperClassName={`totem-date-picker-${name}`}
                            {...props}
                        />
                    )}
                />
                {error ? (
                    <div className="absolute right-[-2px] top-[-16px] text-base text-destructive w-max">{error}</div>
                ) : null}
            </div>
        </div>
    );
};
